import { PageName } from "@/Components/PageLockProvider";
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getImpactRating(health : 1|2|4) {
  switch (health) {
      case 1:
          return "High";
      case 2:
          return "Medium";
      case 4:
          return "Low";
      default:
          return "High";
  }
}

export function getRatingColor(rating: "High" | "Medium" | "Low"): string {
  switch (rating) {
    case "High":
      return "bg-chart-red";
    case "Medium":
      return "bg-chart-amber";
    case "Low":
      return "bg-chart-green";
    default:
      return "bg-chart-green"; // Default color
  }
}

export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case "closed":
      return"bg-chart-green";
    case "open":
      return "bg-chart-amber";
    case "cancelled":
      return "bg-gray-500";
    default:
      return "bg-chart-green"; // Default color
  }
}

export function getRatingNumber(ratingString: "High" | "Medium" | "Low"): 1 | 2 | 4 {
  switch (ratingString) {
    case "High":
      return 1;
    case "Medium":
      return 2;
    case "Low":
      return 4;
    default:
      return 1; // Default to High if unknown
  }
}

export function getRatingCombination(probability: "High" | "Medium" | "Low", impact: "High" | "Medium" | "Low"): string {
  const probabilityNumber = getRatingNumber(probability);
  const impactNumber = getRatingNumber(impact);

  let combination = Math.floor((probabilityNumber + impactNumber) / 2);
  combination = combination === 3 ? 4 : combination; // Adjust for 3 to be treated as Low

  return getImpactRating(combination as 1 | 2 | 4);
}

export function getLifecycleStageName(lifecycleStage: 1 | 2 | 3 | 4): string {
  switch (lifecycleStage) {
    case 1:
      return "Initiation";
    case 2:
      return "Planning";
    case 3:
      return "Execution";
    case 4:
      return "Closure";
    default:
      return "Unknown Stage";
  }
}

export function getPageName(pageName: number): string {
  switch (pageName) {
    case 1:
      return "Plan";
    case 2:
      return "Charter";
    case 3:
      return "Status Report";
    case 4:
      return "Risks";
    case 5:
      return "Issues";
    case 6:
      return "Actions";
    case 7:
      return "Decisions";
    case 8:
      return "Support Requests";
    default:
      return "Unknown Page";
  }
}

export function getPageNameFromEnum(pageName: PageName): string {
  switch (pageName) {
    case PageName.Plan:
      return "Plan";
    case PageName.Charter:
      return "Charter";
    case PageName.StatusReport:
      return "Status Report";
    case PageName.Risks:
      return "Risks";
    case PageName.Issues:
      return "Issues";
    case PageName.Actions:
      return "Actions";
    case PageName.Decisions:
      return "Decisions";
    case PageName.SupportRequests:
      return "Support Requests";
    default:
      return "Unknown Page";
  }
}

// Tutorial tracking using bitwise flags
export enum Tutorial {
  Dashboard = 1,
  RAIDS = 2,
  StatusReport = 4,
  Plan = 8,
  Team = 16,
  Users = 32,
  LockManager = 64,
}

export function getTutorialName(tutorial: Tutorial): string {
  switch (tutorial) {
    case Tutorial.Dashboard:
      return "Dashboard";
    case Tutorial.RAIDS:
      return "RAIDS";
    case Tutorial.StatusReport:
      return "Status Report";
    case Tutorial.Plan:
      return "Plan";
    case Tutorial.Team:
      return "Team";
    case Tutorial.Users:
      return "Users";
    case Tutorial.LockManager:
      return "Lock Manager";
    default:
      return "Unknown Tutorial";
  }
}

export function hasCompletedTutorial(tutorialsCompleted: number, tutorial: Tutorial): boolean {
  return (tutorialsCompleted & tutorial) === tutorial;
}

export function markTutorialCompleted(tutorialsCompleted: number, tutorial: Tutorial): number {
  return tutorialsCompleted | tutorial;
}

export function markTutorialIncomplete(tutorialsCompleted: number, tutorial: Tutorial): number {
  return tutorialsCompleted & ~tutorial;
}